/**
 * Reusable slider component for configuration controls.
 *
 * Features:
 * - Label with current value display
 * - Min/max range with step
 * - Unit suffix (e.g., "req/s", "s")
 * - Dark mode support
 */

import { useId } from 'react';

interface SliderProps {
  /** Label text */
  label: string;
  /** Current value */
  value: number;
  /** Minimum value */
  min: number;
  /** Maximum value */
  max: number;
  /** Step increment */
  step?: number;
  /** Unit suffix displayed after value (e.g., "s", "req/s") */
  unit?: string;
  /** Called when value changes */
  onChange: (value: number) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Optional description text */
  description?: string;
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  unit,
  onChange,
  disabled = false,
  description,
}: SliderProps) {
  const id = useId();

  // Format value for display (handle undefined gracefully)
  const safeValue = value ?? min;
  const displayValue = Number.isInteger(step) ? safeValue : safeValue.toFixed(1);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className="text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
        </label>
        <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
          {displayValue}
          {unit && <span className="text-gray-400 dark:text-gray-500 ml-0.5">{unit}</span>}
        </span>
      </div>

      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}

      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={safeValue}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        disabled={disabled}
        className={`
          w-full h-2 rounded-lg appearance-none cursor-pointer
          bg-gray-200 dark:bg-gray-600
          accent-blue-600 dark:accent-blue-500
          disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          dark:focus:ring-offset-gray-800
        `}
      />

      <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500">
        <span>{min}{unit && unit}</span>
        <span>{max}{unit && unit}</span>
      </div>
    </div>
  );
}

export default Slider;
